import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import {
  asyncStandupPath,
  digestExamplesPath,
  githubDigestPath,
  replaceStandupsPath,
  rootPath,
  signInPath,
  standupBotsPath,
  whatIsAGithubDigestPath,
} from '@/routes'
import { Link } from '@inertiajs/react'
import { ArrowRight, HelpCircleIcon } from 'lucide-react'

const SIGN_IN_CAMPAIGN = 'utm_source=ai-pr-summary-vs-standup&utm_campaign=comparison'

const COPILOT_SUMMARY_DOCS =
  'https://docs.github.com/en/copilot/using-github-copilot/creating-a-pull-request-summary-with-github-copilot'
const COPILOT_SUMMARY_LIMITS = 'https://docs.github.com/en/copilot/responsible-use/chat'
const COPILOT_EXPLORE_PR = 'https://docs.github.com/en/copilot/tutorials/explore-pull-requests'

const linkClass = 'text-foreground decoration-golden-500 underline underline-offset-4'

type Faq = {
  question: string
  answer: string
}

const ROWS = [
  {
    need: 'Read one pull request',
    summary: 'Yes. Prose and bullets from that pull request',
    bot: 'Only if someone describes it',
    digest: 'No. It waits until commits land',
  },
  {
    need: 'Team status for managers',
    summary: 'No. One pull request, for reviewers',
    bot: 'Yes, if each person writes an update',
    digest: 'Yes. Daily or weekly in Slack and email',
  },
  {
    need: 'Several repositories',
    summary: 'No. One pull request in one repository',
    bot: 'Only if someone lists them',
    digest: 'Yes. The repositories you select',
  },
  {
    need: 'A daily cadence',
    summary: 'No. Someone requests each summary',
    bot: 'Yes. The bot asks on a schedule',
    digest: 'Yes. Previous 24 hours, or weekly',
  },
  {
    need: 'The open pull request in front of you',
    summary: 'Yes. That pull request is the input',
    bot: 'Only if someone mentions it',
    digest: 'No, until those commits land',
  },
  {
    need: 'Every open pull request',
    summary: 'No. There is no cross-PR rollup',
    bot: 'Only what people choose to write',
    digest: 'No. Open work is omitted',
  },
  {
    need: 'A push with no pull request',
    summary: 'No. The feature summarizes a pull request',
    bot: 'Only if someone mentions it',
    digest: 'Yes, if the commit landed',
  },
  {
    need: 'Hiring, support, or blockers',
    summary: 'No. It only reads the diff',
    bot: 'Yes. A question can ask',
    digest: 'No. Quiet git is not a quiet day',
  },
] as const

function TextLink({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <Link href={href} className={linkClass}>
      {children}
    </Link>
  )
}

function DocLink({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <a href={href} className={linkClass}>
      {children}
    </a>
  )
}

export function AiPrSummaryVsStandupArticle({ headline, faqs }: { headline: string; faqs: Faq[] }) {
  return (
    <div className="mx-auto max-w-4xl space-y-12 md:space-y-16">
      <header className="space-y-4 text-center">
        <p className="text-golden-500 text-sm font-semibold tracking-wider uppercase">For engineering managers</p>
        <h1 className="text-foreground text-4xl font-bold tracking-tight text-balance sm:text-5xl md:text-6xl">
          {headline}
        </h1>
        <p className="text-muted-foreground mx-auto max-w-2xl text-lg leading-relaxed sm:text-xl">
          Copilot PR summaries help a reviewer read one pull request. That is where single-PR AI stops and a team
          digest starts. <TextLink href={rootPath()}>Git Digest</TextLink> posts what landed across the repositories
          you pick, to Slack and email.
        </p>
      </header>

      <Card className="border-golden-500/20 from-golden-500/5 via-background to-muted/30 bg-gradient-to-br p-6 text-center sm:p-8">
        <p className="text-muted-foreground text-sm leading-relaxed sm:text-base">
          Use an AI PR summary when someone is about to review a pull request. Use a repo digest when a manager needs
          yesterday across repositories.
        </p>
      </Card>

      <section className="space-y-4">
        <h2 className="text-foreground text-3xl tracking-tight sm:text-4xl">What an AI PR summary does well</h2>
        <div className="text-muted-foreground space-y-4 text-base leading-relaxed">
          <p>
            GitHub documents a pull request summary you generate on GitHub.com. Open a new or existing pull request,
            click the Copilot control in the description or comment field, and choose Summary. Copilot builds the
            summary from the code diffs. The result is a prose overview plus a bulleted list of key changes, linked to
            the affected lines. You can put that text in the description or in a comment on the pull request timeline.
            The how-to is{' '}
            <DocLink href={COPILOT_SUMMARY_DOCS}>Creating a pull request summary with GitHub Copilot</DocLink>.
          </p>
          <p>
            GitHub docs also say to start from a blank description, because Copilot does not take existing description
            text into account. Review the summary and add any context reviewers still need. Larger pull requests may
            take up to a couple of minutes. The only supported language for these summaries is English. A file with
            more than 400 combined additions and deletions is left out of the summary. The summary is created only
            when someone asks, and it is not refreshed when the pull request changes. Ask again, then review the new
            text. Those limits are on the{' '}
            <DocLink href={COPILOT_SUMMARY_LIMITS}>GitHub Copilot Chat application card</DocLink>.
          </p>
          <p>
            Copilot Chat on the pull request page can also summarize that same pull request, report its status, or
            explain specific files. GitHub docs say the pull request is the context for that chat. It is still one
            pull request. See{' '}
            <DocLink href={COPILOT_EXPLORE_PR}>Using GitHub Copilot to explore pull requests</DocLink>.
          </p>
        </div>
      </section>

      <section className="space-y-4">
        <h2 className="text-foreground text-3xl tracking-tight sm:text-4xl">Why that is not a team standup</h2>
        <div className="text-muted-foreground space-y-4 text-base leading-relaxed">
          <p>
            A standup, and the status update a manager reads, answers a different question: what did this team ship,
            across people and repositories, since yesterday? An AI PR summary does not answer that.
          </p>
          <p>
            The documented feature is one pull request. There is no cross-PR rollup and no cross-repo rollup. There is
            no daily cadence. Nobody receives a morning post because a pull request existed. A commit that never
            became a pull request, including a direct push to the default branch, is outside the summary.
          </p>
          <p>
            GitHub describes the summary as help for reviewers of that pull request. A founder or an engineering
            manager who wants a team status is asking for a different artifact. You can summarize the open pull
            request you have on screen. You do not get a list of every open pull request, who is blocked, or what
            never reached GitHub.
          </p>
          <p>
            A standup bot can collect the human parts: blockers, hiring, support, and work still in review. A form is
            the right tool when the update is not in git. The{' '}
            <TextLink href={standupBotsPath()}>standup bots comparison</TextLink> and the{' '}
            <TextLink href={asyncStandupPath()}>async standup playbook</TextLink> cover that case.
          </p>
        </div>
      </section>

      <section id="decision-matrix" className="space-y-4">
        <h2 className="text-foreground text-3xl tracking-tight sm:text-4xl">Decision matrix</h2>
        <p className="text-muted-foreground text-base leading-relaxed">
          Match the question to the tool. An AI PR summary, a status bot, and a repo digest are not substitutes.
        </p>
        <div className="overflow-x-auto rounded-xl border">
          <table className="w-full min-w-[52rem] text-left text-sm">
            <caption className="bg-muted/40 text-muted-foreground px-4 py-3 text-left text-xs leading-relaxed">
              Need against an AI PR summary, a standup bot, and a repo digest of commits that landed.
            </caption>
            <thead className="bg-muted/50 text-foreground">
              <tr>
                <th scope="col" className="px-4 py-3 font-semibold">
                  Need
                </th>
                <th scope="col" className="px-4 py-3 font-semibold">
                  AI PR summary
                </th>
                <th scope="col" className="px-4 py-3 font-semibold">
                  Standup bot
                </th>
                <th scope="col" className="px-4 py-3 font-semibold">
                  Repo digest
                </th>
              </tr>
            </thead>
            <tbody>
              {ROWS.map((row) => (
                <tr key={row.need} className="border-t">
                  <th scope="row" className="text-foreground px-4 py-3 font-medium">
                    {row.need}
                  </th>
                  <td className="text-muted-foreground px-4 py-3">{row.summary}</td>
                  <td className="text-muted-foreground px-4 py-3">{row.bot}</td>
                  <td className="text-muted-foreground px-4 py-3">{row.digest}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-muted-foreground text-base leading-relaxed">
          If the row you care about is what landed in the selected repositories, that is a repo digest. Read{' '}
          <TextLink href={whatIsAGithubDigestPath()}>what a GitHub digest is</TextLink>, then the{' '}
          <TextLink href={digestExamplesPath()}>digest examples</TextLink>. The workflow for sending that delivery
          instead of a status meeting is <TextLink href={replaceStandupsPath()}>replace standups</TextLink>. The{' '}
          <TextLink href={githubDigestPath()}>GitHub digest comparison</TextLink> separates this product from
          Gitingest, which turns a repository into text for a language model.
        </p>
      </section>

      <section className="space-y-4">
        <h2 className="text-foreground text-3xl tracking-tight sm:text-4xl">Where a repo digest fits</h2>
        <div className="text-muted-foreground space-y-4 text-base leading-relaxed">
          <p>
            Git Digest is the repo digest in that table. Sign in with GitHub and choose the repositories. Only those
            repos are included. A daily digest covers the previous 24 hours. A weekly digest covers the past week and
            is prepared on Mondays. Email goes to workspace members who have the email digest turned on, at the email
            hour you set. Slack goes to the channel stored when you connect Slack, at the Slack hour you set.
          </p>
          <p>
            Each change is a short summary, the author, and a link back to GitHub. When the change belongs to a pull
            request, the link uses that pull request number. Otherwise it uses a short commit id, which is how a
            direct push shows up. Slack groups the lines under Feature, Bugfix, Improvement, Chore, and Refactoring.
            Email shows the first five changes in each repository, with lines added and removed. If the window is
            empty, Slack says there were no code changes.
          </p>
          <p>
            The digest reports commits that landed. An open pull request that has not landed is still a job for the
            PR summary and the reviewer, not for this morning&apos;s digest. Keep a short note when the work never
            becomes a commit.
          </p>
          <p>
            Digests are prepared for workspaces with an active subscription. The homepage Unlimited plan is $29 per
            month, or $25 per month on yearly billing, for the team rather than per seat. A lifetime option is $1,000
            once.
          </p>
        </div>
      </section>

      <section className="space-y-6">
        <div>
          <h2 className="text-foreground text-3xl tracking-tight sm:text-4xl">AI PR summary FAQ</h2>
          <p className="text-muted-foreground mt-3 text-base">
            Short answers on Copilot pull request summaries, standup bots, and a daily GitHub digest.
          </p>
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          {faqs.map((faq) => (
            <Card key={faq.question} className="h-full">
              <CardContent className="pt-6">
                <h3 className="text-foreground mb-3 flex items-start gap-x-3 text-lg">
                  <span className="flex h-[1lh] items-center">
                    <HelpCircleIcon className="text-golden-500 size-4 shrink-0" />
                  </span>
                  {faq.question}
                </h3>
                <p className="text-muted-foreground text-sm leading-relaxed">{faq.answer}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      <Card className="border-orange-200/50 bg-gradient-to-br from-orange-50 to-orange-100/30 p-8 text-center dark:border-orange-800/30 dark:from-orange-500/10 dark:to-orange-500/5">
        <h2 className="mb-3 text-2xl text-gray-900 dark:text-white">Send the team what landed</h2>
        <p className="text-muted-foreground mx-auto mb-6 max-w-2xl text-sm">
          Connect GitHub, pick the repositories, and read the digest in Slack or email.
        </p>
        <Button
          asChild
          size="lg"
          className="bg-golden-500 hover:bg-golden-600 font-semibold text-black shadow-lg hover:shadow-xl"
        >
          <Link href={`${signInPath()}?${SIGN_IN_CAMPAIGN}`}>
            Start with GitHub
            <ArrowRight className="ml-2 size-4 shrink-0" />
          </Link>
        </Button>
      </Card>
    </div>
  )
}
